// =============================================================================
// Consola /platform (Entrega 1a) — consola de trabajo para UNA persona (Arturo).
// Alcance EXACTO (relay 2026-09-26): (1) lista de tenants, (2) precios de lista
// solo lectura, (3) contador de cupos Fundador, (4) aplicar/revocar descuento +
// registrar pago manual, (5) auditoría. Sin buscador, filtros, gráficas ni export.
// Autenticada (Bearer de la sesión); el backend enforcea platform-admin (403).
// =============================================================================
import { Fragment, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { SignupFunnelCard } from './SignupFunnelCard';
import { OwnerSupportPanel } from './OwnerSupportPanel';
import { UserQuotaPanel } from './UserQuotaPanel';

export const isPlatformRoute = (p: string) => p === '/platform' || p.startsWith('/platform/');

const errMsg = (e: any, fallback: string): string => {
  const m = e?.body?.message;
  return Array.isArray(m) ? m.join(' ') : m || e?.message || fallback;
};
// REGLA (no filtro de UI): los tenants desechables que crean los gates
// (nombre "E2E Workspace…" / "E2E onepath…") NO aparecen en la consola, igual
// que is_internal. En prod los reales no matchean; en staging quita el ruido.
const DISPOSABLE_RE = /^E2E/i;
const isDisposable = (name: string | undefined) => DISPOSABLE_RE.test(name ?? '');

const pesos = (cents: number | null | undefined) =>
  cents == null ? '—' : `$${(cents / 100).toLocaleString('es-MX', { minimumFractionDigits: 0 })}`;
const fecha = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString('es-MX') : '—';
// Etiqueta del tipo de descuento (fundador = de por vida; adopcion = 1er año, vence).
const discLabel = (t: string): string =>
  t === 'fundador' ? 'Fundador' : t === 'adopcion' ? 'Adopción temprana' : t;
// ¿Este descuento VENCE? (v14 §2.1). La vigencia se decide por los DATOS, no por un
// texto fijo de Fundador: granted ⟺ ends_at != null (adopción trae fecha, Fundador NULL).
// En reserved ends_at aún es NULL (se fija al otorgar) → el único dato que dice si vencerá
// es el tipo; es la única vez que entra, e inevitable (el dato de vigencia no existe todavía).
const discountExpires = (d: { type: string; status: string; ends_at: string | null }): boolean =>
  d.ends_at != null || (d.status !== 'granted' && d.type === 'adopcion');

interface Tenant {
  tenantId: string; name: string; slug: string; plan: string;
  subscriptionActive: boolean; manuallyCovered: boolean; manualPaidUntil: string | null;
  trialEndsAt: string | null;
  // PR A (V204) — folios de la bolsa anual (consumo/total + % + renovacion + inicio suscripcion).
  foliosConsumed?: number; foliosIncludedTotal?: number | null; foliosPct?: number | null;
  foliosRenewAt?: string | null; subscriptionStartedAt?: string | null;
}
interface ListPrice {
  vertical: string; tier: string; billing_interval: string; amount_cents: number; branch_pct: string | null;
}
interface Cupo { otorgados: number; reservados: number; libres: number; total: number }
interface Discount {
  tenant_id: string; type: string; status: string; percent: string;
  list_price_snapshot_cents: number | null; ends_at: string | null; applied_at: string; granted_at: string | null; note: string | null;
}
interface ChargePreview {
  tenantId: string; isInternal: boolean; subscriptionActive: boolean; vertical: string | null;
  billingInterval: string | null; intervalIndeterminate: boolean; activeBranches: number;
  listBaseCents: number | null; listBranchCents: number | null; subtotalCents: number | null;
  effectivePercent: number; representablePercent: number; priceToChargeCents: number | null;
  divergentCombined: boolean; divergentStripe: boolean | null;
}

const estadoTenant = (t: Tenant): string => {
  if (t.subscriptionActive) return 'Suscripción activa';
  if (t.manuallyCovered) return `Pago manual (hasta ${fecha(t.manualPaidUntil)})`;
  if (t.trialEndsAt) return `Prueba (hasta ${fecha(t.trialEndsAt)})`;
  return 'Sin cobertura';
};

export function PlatformConsolePage({ onLogout }: { onLogout: () => void }) {
  const [loading, setLoading] = useState(true);
  const [fatal, setFatal] = useState<string | null>(null);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [prices, setPrices] = useState<ListPrice[]>([]);
  const [cupo, setCupo] = useState<Cupo | null>(null);
  const [adopcionCupo, setAdopcionCupo] = useState<Cupo | null>(null);
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [charge, setCharge] = useState<Map<string, ChargePreview>>(new Map());
  const [openId, setOpenId] = useState<string | null>(null); // tenant con panel de acciones abierto
  const [busy, setBusy] = useState(false);
  const [rowErr, setRowErr] = useState<string | null>(null);
  const [rowOk, setRowOk] = useState<string | null>(null);

  const load = async () => {
    const [t, p, c, d, cp, ac] = await Promise.all([
      api<{ tenants?: Tenant[] } | Tenant[]>('/platform/tenants'),
      api<any[]>('/platform/pricing/list-prices'),
      api<Cupo>('/platform/pricing/founder-cupo'),
      api<any[]>('/platform/discounts'),
      api<ChargePreview[]>('/platform/pricing/charge-preview'),
      api<Cupo>('/platform/pricing/adopcion-cupo'),
    ]);
    const allTenants = (Array.isArray(t) ? t : (t as any).tenants ?? []) as Tenant[];
    const real = allTenants.filter((x) => !isDisposable(x.name));   // regla: sin desechables
    const realIds = new Set(real.map((x) => x.tenantId));
    setTenants(real);
    setPrices(p as ListPrice[]);
    setCupo(c);
    setAdopcionCupo(ac);
    setDiscounts((d as Discount[]).filter((x) => realIds.has(x.tenant_id)));
    setCharge(new Map((cp as ChargePreview[]).map((x) => [x.tenantId, x])));
  };

  useEffect(() => {
    setLoading(true);
    load()
      .catch((e: any) => setFatal(e?.status === 403 ? 'Esta consola requiere platform-admin.' : errMsg(e, 'No se pudo cargar la consola.')))
      .finally(() => setLoading(false));
  }, []);

  const refresh = async (msg: string) => {
    setRowErr(null); setRowOk(null);
    try { await load(); setRowOk(msg); } catch (e: any) { setRowErr(errMsg(e, 'Error al recargar.')); }
  };

  const activeDiscount = (tenantId: string) =>
    discounts.find((d) => d.tenant_id === tenantId && (d.status === 'reserved' || d.status === 'granted')) ?? null;

  const box = 'rounded-2xl border border-slate-200 bg-white p-5';
  const th = 'px-3 py-2 text-left text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400';
  const td = 'px-3 py-2 text-sm text-slate-700';

  if (loading) return <div className="p-8 text-sm text-slate-500">Cargando consola…</div>;
  if (fatal) {
    return (
      <div className="mx-auto max-w-lg p-8">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">{fatal}</div>
        <button onClick={onLogout} className="mt-4 text-xs text-slate-500 underline">Cerrar sesión</button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6 md:p-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-2xl tracking-tight text-slate-900">Consola de plataforma</h1>
          <p className="text-sm text-slate-500">Precios, cupos y descuentos. Consola interna.</p>
        </div>
        <a href="/" className="text-xs text-slate-500 underline">← Volver al panel</a>
      </div>

      {/* §1bis.9 — embudo del alta (medición propia, sin terceros). */}
      <SignupFunnelCard />

      {/* (3) Contadores de cupo — Fundador (de por vida) + Adopción temprana (1er año) */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {cupo && (
          <div className={box}>
            <p className="mb-3 text-sm font-medium text-slate-800">Cupo Fundador (de {cupo.total})</p>
            <div className="flex gap-6">
              <Stat n={cupo.otorgados} label="Otorgados" tone="emerald" />
              <Stat n={cupo.reservados} label="Reservados" tone="amber" />
              <Stat n={cupo.libres} label="Libres" tone="slate" />
            </div>
          </div>
        )}
        {adopcionCupo && (
          <div className={box}>
            <p className="mb-3 text-sm font-medium text-slate-800">Cupo Adopción temprana (de {adopcionCupo.total})</p>
            <div className="flex gap-6">
              <Stat n={adopcionCupo.otorgados} label="Otorgados" tone="emerald" />
              <Stat n={adopcionCupo.reservados} label="Reservados" tone="amber" />
              <Stat n={adopcionCupo.libres} label="Libres" tone="slate" />
            </div>
          </div>
        )}
      </div>

      {/* Leyenda de los cupos (PR A): qué significan los contadores y qué libera cupo. */}
      {(cupo || adopcionCupo) && (
        <div className="rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 text-xs text-slate-600">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
            <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-emerald-500 align-middle" /><strong>Otorgados</strong>: descuento activo (tras el 1er pago).</span>
            <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-amber-500 align-middle" /><strong>Reservados</strong>: prometido, se activa al 1er pago.</span>
            <span><span className="mr-1 inline-block h-2 w-2 rounded-full bg-slate-400 align-middle" /><strong>Libres</strong>: cupo restante.</span>
          </div>
          <p className="mt-1.5 text-slate-500">
            El cupo cuenta <strong>personas</strong>. <strong>Fundador</strong> no vence (de por vida). <strong>Adopción temprana</strong> vence
            1 año después del 1er pago; una adopción <strong>vencida sigue ocupando cupo</strong> — solo <strong>revocar</strong> libera un lugar.
          </p>
        </div>
      )}

      {/* (2) Precios de lista, solo lectura */}
      <div className={box}>
        <p className="mb-1 text-sm font-medium text-slate-800">Precios de lista <span className="text-slate-400">(solo lectura)</span></p>
        <p className="mb-3 text-[11px] text-amber-700">
          Solo lectura <strong>por diseño</strong>: hoy el monto base que cobra Stripe sale de su objeto <code>Price</code> (del seed), no de aquí.
          Editar este valor <strong>no cambiaría lo que se cobra</strong> hasta que el seed lea la base (1b). Solo el descuento se aplica desde el motor.
        </p>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead><tr>
              <th className={th}>Vertical</th><th className={th}>Nivel</th><th className={th}>Intervalo</th>
              <th className={th}>Precio</th><th className={th}>Sucursal +%</th>
            </tr></thead>
            <tbody>
              {prices.map((p, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className={td}>{p.vertical}</td><td className={td}>{p.tier}</td>
                  <td className={td}>{p.billing_interval === 'monthly' ? 'Mensual' : 'Anual'}</td>
                  <td className={td}>{pesos(p.amount_cents)}</td>
                  <td className={td}>{p.branch_pct == null ? '—' : `${Math.round(Number(p.branch_pct) * 100)}%`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {rowErr && <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm text-rose-700">{rowErr}</div>}
      {rowOk && <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">{rowOk}</div>}

      {/* (1) Lista de tenants + (4) acciones + (5) auditoría por fila */}
      <div className={box}>
        <p className="mb-3 text-sm font-medium text-slate-800">Tenants ({tenants.length})</p>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead><tr>
              <th className={th}>Negocio</th><th className={th}>Plan</th><th className={th}>Estado</th>
              <th className={th}>Descuento vigente</th><th className={th}>Precio a cobrar</th>
              <th className={th}>Folios</th><th className={th}>Renueva</th><th className={th}>Inicio suscripción</th>
              <th className={th}></th>
            </tr></thead>
            <tbody>
              {tenants.map((t) => {
                const d = activeDiscount(t.tenantId);
                return (
                  <Fragment key={t.tenantId}>
                    <tr className="border-t border-slate-100">
                      <td className={td}>{t.name}</td>
                      <td className={td}>{t.plan}</td>
                      <td className={td}>{estadoTenant(t)}</td>
                      <td className={td}>
                        {d ? (
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${d.status === 'granted' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}
                            title={d.type === 'adopcion' ? 'Adopción temprana: 30% el primer año; vence en la fecha indicada.' : 'Fundador: 30% de por vida; solo registras el pago cada mes.'}>
                            {discLabel(d.type)} · {Math.round(Number(d.percent))}% ·{' '}
                            {d.status !== 'granted'
                              ? 'reservado (se activa al 1er pago)'
                              : d.type === 'adopcion'
                                ? `vigente · vence ${fecha(d.ends_at)}`
                                : `vigente desde ${fecha(d.granted_at ?? d.applied_at)} · no vence`}
                          </span>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className={td}><ChargeCell cp={charge.get(t.tenantId)} /></td>
                      <td className={td}>
                        {t.foliosIncludedTotal == null ? (
                          <span className="text-slate-400">—</span>
                        ) : (
                          <span title={`${t.foliosPct ?? 0}% del periodo`}>
                            {(t.foliosConsumed ?? 0).toLocaleString('es-MX')} / {t.foliosIncludedTotal.toLocaleString('es-MX')}
                          </span>
                        )}
                      </td>
                      <td className={td}>{fecha(t.foliosRenewAt)}</td>
                      <td className={td}>{fecha(t.subscriptionStartedAt)}</td>
                      <td className={`${td} text-right`}>
                        <button onClick={() => { setOpenId(openId === t.tenantId ? null : t.tenantId); setRowErr(null); setRowOk(null); }}
                          className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50">
                          {openId === t.tenantId ? 'Cerrar' : 'Acciones'}
                        </button>
                      </td>
                    </tr>
                    {openId === t.tenantId && (
                      <tr className="bg-slate-50/60"><td colSpan={9} className="px-3 py-4">
                        <TenantActions tenant={t} discount={d} cupo={cupo} adopcionCupo={adopcionCupo} busy={busy} setBusy={setBusy}
                          onDone={refresh} setRowErr={setRowErr} />
                      </td></tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* (5) Auditoría — descuentos activos, con snapshot y cuándo */}
      <div className={box}>
        <p className="mb-3 text-sm font-medium text-slate-800">Auditoría de descuentos activos</p>
        {discounts.length === 0 ? <p className="text-sm text-slate-400">Sin descuentos activos.</p> : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead><tr>
                <th className={th}>Tenant</th><th className={th}>Tipo</th><th className={th}>%</th>
                <th className={th}>Estado</th><th className={th}>Sobre lista</th><th className={th}>Aplicado</th><th className={th}>Otorgado</th><th className={th}>Nota</th>
              </tr></thead>
              <tbody>
                {discounts.map((d, i) => {
                  const t = tenants.find((x) => x.tenantId === d.tenant_id);
                  return (
                    <tr key={i} className="border-t border-slate-100">
                      <td className={td}>{t?.name ?? d.tenant_id.slice(0, 8)}</td>
                      <td className={td}>{d.type}</td>
                      <td className={td}>{Math.round(Number(d.percent))}%</td>
                      <td className={td}>{d.status}</td>
                      <td className={td}>{pesos(d.list_price_snapshot_cents)}</td>
                      <td className={td}>{fecha(d.applied_at)}</td>
                      <td className={td}>{fecha(d.granted_at)}</td>
                      <td className={`${td} max-w-xs truncate`} title={d.note ?? ''}>{d.note ?? '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// §5.3 — precio a cobrar (lista − descuento efectivo, con sucursales) + avisos de
// divergencia: combinado no representable por un cupón / ≠ Price de Stripe.
function ChargeCell({ cp }: { cp: ChargePreview | undefined }) {
  if (cp?.isInternal) return <span className="text-slate-400" title="Tenant interno: no consume cupo ni se cobra.">interno · no se cobra</span>;
  if (cp?.intervalIndeterminate) return (
    <span className="inline-flex rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700"
      title="Suscrito por Stripe pero sin intervalo conocido: no se computa el precio para no adivinar mensual/anual.">
      ⚠ intervalo indeterminado
    </span>
  );
  if (!cp || cp.priceToChargeCents == null) return <span className="text-slate-400">—</span>;
  const branchNote = cp.activeBranches > 1 ? ` · ${cp.activeBranches} suc.` : '';
  // billingInterval null = sin sub Stripe → el precio es REFERENCIA mensual.
  const intervalNote = cp.billingInterval === 'yearly' ? '/año' : cp.billingInterval === 'monthly' ? '/mes' : '/mes ref.';
  return (
    <div className="space-y-1">
      <div className="text-sm text-slate-800">
        {pesos(cp.priceToChargeCents)}<span className="text-slate-400">{intervalNote}</span>
        {cp.effectivePercent > 0 && <span className="text-slate-400"> · −{cp.effectivePercent}%</span>}
        <span className="text-slate-400">{branchNote}</span>
      </div>
      {cp.divergentCombined && (
        <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-800"
          title={`El motor computa ${cp.effectivePercent}% pero un solo cupón cobra ${cp.representablePercent}% (combinado no representable). El swap de cupón por período es 1b; hasta entonces el cobro aplica ${cp.representablePercent}%.`}>
          ⚠ combinado {cp.effectivePercent}% ≠ cupón {cp.representablePercent}%
        </span>
      )}
      {cp.divergentStripe === true && (
        <span className="inline-flex rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-medium text-rose-700"
          title="El precio de lista de la base (esta consola) no coincide con el unit_amount del Price de Stripe, que es quien cobra la base hasta el seed-desde-DB (1b).">
          ⚠ ≠ Price de Stripe
        </span>
      )}
      {cp.divergentStripe === null && (
        <span className="text-[10px] text-slate-400" title="Este ambiente no tiene Stripe configurado; no se pudo comparar contra el Price real.">
          sin verificar vs Stripe
        </span>
      )}
    </div>
  );
}

function Stat({ n, label, tone }: { n: number; label: string; tone: 'emerald' | 'amber' | 'slate' }) {
  const c = tone === 'emerald' ? 'text-emerald-600' : tone === 'amber' ? 'text-amber-600' : 'text-slate-600';
  return (
    <div>
      <div className={`text-3xl font-semibold ${c}`}>{n}</div>
      <div className="text-[11px] uppercase tracking-[0.14em] text-slate-400">{label}</div>
    </div>
  );
}

// (4) Acciones por tenant: aplicar/revocar Fundador + registrar pago manual.
function TenantActions({ tenant, discount, cupo, adopcionCupo, busy, setBusy, onDone, setRowErr }: {
  tenant: Tenant; discount: Discount | null; cupo: Cupo | null; adopcionCupo: Cupo | null; busy: boolean;
  setBusy: (b: boolean) => void; onDone: (msg: string) => Promise<void>; setRowErr: (s: string | null) => void;
}) {
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [pay, setPay] = useState({ method: 'transferencia', amount: '', paidAt: '', paidUntil: '' });
  const input = 'w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm';
  const lbl = 'text-[11px] uppercase tracking-[0.14em] text-slate-400';

  const run = async (fn: () => Promise<void>, msg: string) => {
    setBusy(true); setRowErr(null);
    try { await fn(); await onDone(msg); } catch (e: any) { setRowErr(errMsg(e, 'No se pudo completar.')); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      {/* V217 (§1ter, D16) — titular: cambio por soporte, congelar / descongelar. */}
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <p className="mb-2 text-xs font-medium text-slate-700">Titular (soporte)</p>
        <OwnerSupportPanel tenantId={tenant.tenantId} />
      </div>
      {/* V219 — tope de usuarios nuevos en 30 días: soporte lo sube con motivo. */}
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <p className="mb-2 text-xs font-medium text-slate-700">Usuarios nuevos (soporte)</p>
        <UserQuotaPanel tenantId={tenant.tenantId} />
      </div>
      {/* Descuento */}
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <p className="mb-2 text-xs font-medium text-slate-700">Descuento</p>
        {discount ? (
          <>
            <p className="mb-1 text-xs text-slate-500">Vigente: {discLabel(discount.type)} · {Math.round(Number(discount.percent))}% · {discount.status}{discount.ends_at ? ` · vence ${fecha(discount.ends_at)}` : ''}.</p>
            {discountExpires(discount) ? (
              <p className="mb-2 rounded-lg bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
                {discount.ends_at
                  ? <><strong>Vence el {fecha(discount.ends_at)}</strong>. Al vencer, la suscripción vuelve al precio de lista (el cobro deja de aplicar el descuento).</>
                  : <><strong>30% el primer año</strong> desde el primer pago. Al marcarlo como otorgado se fija la fecha de vencimiento (un año después).</>}
              </p>
            ) : (
              <p className="mb-2 rounded-lg bg-emerald-50 px-2 py-1 text-[11px] text-emerald-700">
                Persistente: se aplicó una vez y <strong>no vence</strong>. Cada mes solo registras el pago; no hay que reaplicarlo. Revocar es solo si lo decides a propósito.
              </p>
            )}
            {/* §3 (relay v6) — otorgar: reserved→granted al confirmar el pago. El webhook de
                Stripe NO lo mueve (solo el pago manual lo dispara por trigger); este botón
                llama a fn_platform_grant_founder (DEFINER, auditado con granted_by). */}
            {discount.status === 'reserved' && (
              <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50/50 p-2">
                <p className="mb-2 text-[11px] text-emerald-800">
                  <strong>Reservado</strong> = prometido, aún sin pago confirmado. Al confirmarse el 1er pago (por Stripe), <strong>márcalo como otorgado</strong> para que el contador lo cuente como pagado.
                </p>
                <button disabled={busy}
                  onClick={() => run(() => api(`/platform/tenants/${tenant.tenantId}/discounts/grant-founder`, { method: 'POST' }), 'Fundador otorgado (marcado como pagado).')}
                  className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
                  Marcar como otorgado (pagado)
                </button>
              </div>
            )}
            <label className={lbl}>Motivo de la revocación</label>
            <input className={input} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="p. ej. nunca pagó" />
            <button disabled={busy || !reason.trim()}
              onClick={() => run(() => api(`/platform/tenants/${tenant.tenantId}/discounts/revoke`, { method: 'POST', body: { reason: reason.trim() } }), 'Descuento revocado.')}
              className="mt-2 rounded-lg px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50">Revocar descuento</button>
          </>
        ) : (
          <>
            {/* Candado LEVANTADO (1a §5.2): el paso 3 cableó billing.service para leer
                el MOTOR (tenant_discounts → cupón fundador30, 30% de por vida). Aplicar
                Fundador aquí SÍ se cobra por Stripe. Para pago manual, guíate por el
                "precio a cobrar" de la tabla. */}
            <p className="mb-2 rounded-lg bg-emerald-50 px-2 py-1 text-[11px] text-emerald-700">
              <strong>Fundador</strong> = 30% de por vida · <strong>Adopción temprana</strong> = 30% el primer año (vence). El cobro por Stripe ya los aplica (motor → cupón); en pago manual, usa el <strong>precio a cobrar</strong> de la tabla.
            </p>
            {cupo && cupo.libres <= 0 && (
              <p className="mb-2 rounded-lg bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
                <strong>Fundador {cupo.otorgados + cupo.reservados + 1} de {cupo.total}.</strong> El cupo es un contador, no un candado: se registrará de todos modos y queda auditado. (Decisión de Arturo.)
              </p>
            )}
            <label className={lbl}>Nota (opcional)</label>
            <input className={input} value={note} onChange={(e) => setNote(e.target.value)} placeholder="referencia interna" />
            <div className="mt-2 flex flex-wrap gap-2">
              <button disabled={busy}
                onClick={() => run(() => api(`/platform/tenants/${tenant.tenantId}/discounts/apply`, { method: 'POST', body: { type: 'fundador', note: note.trim() || undefined } }),
                  cupo && cupo.libres <= 0 ? `Fundador aplicado (${cupo.otorgados + cupo.reservados + 1} de ${cupo.total}, sobre cupo — auditado).` : 'Fundador aplicado (30% de por vida).')}
                className="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50">Aplicar Fundador</button>
              {/* V198 — Adopción temprana: 30% PRIMER AÑO (vence). Se otorga igual que Fundador. */}
              <button disabled={busy}
                onClick={() => run(() => api(`/platform/tenants/${tenant.tenantId}/discounts/apply`, { method: 'POST', body: { type: 'adopcion', note: note.trim() || undefined } }),
                  adopcionCupo && adopcionCupo.libres <= 0 ? `Adopción aplicada (${adopcionCupo.otorgados + adopcionCupo.reservados + 1} de ${adopcionCupo.total}, sobre cupo — auditado).` : 'Adopción temprana aplicada (30% primer año).')}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 disabled:opacity-50">Aplicar Adopción temprana</button>
            </div>
            {adopcionCupo && adopcionCupo.libres <= 0 && (
              <p className="mt-2 rounded-lg bg-amber-50 px-2 py-1 text-[11px] text-amber-800">
                <strong>Adopción {adopcionCupo.otorgados + adopcionCupo.reservados + 1} de {adopcionCupo.total}.</strong> Contador, no candado: se registra igual, auditado.
              </p>
            )}
          </>
        )}
      </div>

      {/* Pago manual */}
      <div className="rounded-xl border border-slate-200 bg-white p-3">
        <p className="mb-2 text-xs font-medium text-slate-700">Registrar pago manual</p>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className={lbl}>Método</label>
            <select className={input} value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })}>
              <option value="transferencia">Transferencia</option>
              <option value="efectivo">Efectivo</option>
            </select>
          </div>
          <div><label className={lbl}>Monto</label><input className={input} inputMode="decimal" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} placeholder="0.00" /></div>
          <div><label className={lbl}>Pagó el</label><input type="date" className={input} value={pay.paidAt} onChange={(e) => setPay({ ...pay, paidAt: e.target.value })} /></div>
          <div><label className={lbl}>Cubre hasta</label><input type="date" className={input} value={pay.paidUntil} onChange={(e) => setPay({ ...pay, paidUntil: e.target.value })} /></div>
        </div>
        <button disabled={busy || !pay.amount || !pay.paidAt || !pay.paidUntil}
          onClick={() => run(() => api(`/platform/tenants/${tenant.tenantId}/manual-payments`, { method: 'POST', body: { method: pay.method, amount: Number(pay.amount), paidAt: pay.paidAt, paidUntil: pay.paidUntil } }), 'Pago registrado (si había Fundador reservado, quedó otorgado).')}
          className="mt-2 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50">Registrar pago</button>
      </div>
    </div>
  );
}
